import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { CssBaseline, ThemeProvider, createTheme, CircularProgress, Box } from '@mui/material';

import reportWebVitals from './reportWebVitals';
import './index.css';
import { preloadCriticalResources } from './optimizations';

const App = lazy(() => import('./App'));

const Loading = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100vw',
      backgroundColor: '#F7F8FA',
    }}
  >
    <CircularProgress size={60} thickness={4} />
  </Box>
);

const sendToAnalytics = (metric: any) => {
  if (import.meta.env.DEV) {
    console.log(metric);
  }

  try {
    const metrics = JSON.parse(localStorage.getItem('performance_metrics') || '[]');
    metrics.push({ name: metric.name, value: metric.value, timestamp: Date.now() });
    localStorage.setItem('performance_metrics', JSON.stringify(metrics));
  } catch (error) {
    console.error('Error storing metrics:', error);
  }
};

preloadCriticalResources();

const theme = createTheme({
  palette: {
    primary: {
      main: '#1F5B41',
      light: '#3B7B60',
      dark: '#164332',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2F7AB8',
      light: '#5C9BD0',
      dark: '#215B89',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#C95C39',
    },
    background: {
      default: '#F4F6F2',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1F2933',
      secondary: '#5D6B78',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Helvetica',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.8rem',
      lineHeight: 1.1,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.2rem',
      lineHeight: 1.15,
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.2,
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.25,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
    },
    MuiPaper: {
      defaultProps: { elevation: 2 },
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: '0 6px 20px rgba(15, 23, 42, 0.06)',
          transition: 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          boxShadow: 'none',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2F7AB8',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
          },
        },
      },
    },
  },
});

const rootElement = document.getElementById('root') as HTMLElement;
const root = ReactDOM.createRoot(rootElement);

root.render(
  import.meta.env.DEV ? (
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Suspense fallback={<Loading />}>
          <App />
        </Suspense>
      </ThemeProvider>
    </React.StrictMode>
  ) : (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Suspense fallback={<Loading />}>
        <App />
      </Suspense>
    </ThemeProvider>
  )
);

setTimeout(() => {
  reportWebVitals(sendToAnalytics);
}, 0);

if ('serviceWorker' in navigator) {
  const cleanupLegacyServiceWorkers = async () => {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));

      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames
            .filter((cacheName) => cacheName.startsWith('smartbirds-cache'))
            .map((cacheName) => caches.delete(cacheName))
        );
      }
    } catch (error) {
      console.warn('Legacy service worker cleanup failed:', error);
    }
  };

  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(cleanupLegacyServiceWorkers);
  } else {
    (window as Window).addEventListener('load', cleanupLegacyServiceWorkers);
  }
}

// Optimized performance observers for Core Web Vitals with lazy analytics
if ('PerformanceObserver' in window) {
  // LCP (Largest Contentful Paint)
  const lcpObserver = new PerformanceObserver(list => {
    list.getEntries().forEach(entry => {
      const metric = {
        name: 'LCP',
        value: entry.startTime,
        delta: entry.startTime,
        timestamp: Date.now()
      };
      
      if (import.meta.env.DEV) {
        console.log(`LCP: ${entry.startTime}`, entry);
      }
      
      localStorage.setItem('lcp_metric', JSON.stringify(metric));
      sendToAnalytics(metric);
    });
  });
  lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
  
  // FID (First Input Delay)
  const fidObserver = new PerformanceObserver(list => {
    list.getEntries().forEach(entry => {
      // PerformanceEventTiming interface has processingStart property
      const eventTiming = entry as unknown as {processingStart: number; startTime: number};
      const value = eventTiming.processingStart - eventTiming.startTime;
      const metric = {
        name: 'FID',
        value: value,
        delta: value,
        timestamp: Date.now()
      };
      
      if (import.meta.env.DEV) {
        console.log(`FID: ${value}`, entry);
      }
      
      localStorage.setItem('fid_metric', JSON.stringify(metric));
      sendToAnalytics(metric);
    });
  });
  fidObserver.observe({ type: 'first-input', buffered: true });
  
  // CLS (Cumulative Layout Shift)
  let clsValue = 0;
  let clsEntries: PerformanceEntry[] = [];
  
  const clsObserver = new PerformanceObserver(list => {
    for (const entry of list.getEntries()) {
      // Need to cast entry to LayoutShift type for hadRecentInput and value properties
      const layoutShift = entry as unknown as {hadRecentInput: boolean; value: number};
      
      // Only count layout shifts without recent user input
      if (!layoutShift.hadRecentInput) {
        const firstSessionEntry = clsEntries.length === 0;
        const entrySinceLastSession = clsEntries.length > 0 && 
          entry.startTime - clsEntries[clsEntries.length - 1].startTime > 5000;
          
        if (firstSessionEntry || entrySinceLastSession) {
          clsValue = layoutShift.value;
        } else {
          clsValue += layoutShift.value;
        }
        
        clsEntries.push(entry);
        
        const metric = {
          name: 'CLS',
          value: clsValue,
          delta: layoutShift.value,
          timestamp: Date.now()
        };
        
        if (import.meta.env.DEV) {
          console.log(`CLS: ${clsValue}`, entry);
        }
        
        localStorage.setItem('cls_metric', JSON.stringify(metric));
        sendToAnalytics(metric);
      }
    }
  });
  clsObserver.observe({ type: 'layout-shift', buffered: true });
}
